//@flow

import Search from "./filters/search";
import DateRange from "./filters/date_range";
import TagSearch from "./filters/tag_search";

import type DataTables from 'datatables.net';
import type BaseFilter from './filters/base_filter';

export type FilterFunctions = {
    search: (searchText: ?string) => void,
    tagSearch: (value: ?string, category: ?string) => void,
    dateRange: (minDate: ?Date, maxDate: ?Date) => void
}

export default class FilterContext {
    id: number;
    async: boolean;
    table: DataTables.Api;
    filters: {search: Search, dateRange: DateRange, tagSearch: TagSearch};
    columnIndexes: {[string]: number};
    functions: ((settings: any, data: string[],
                 dataIndex: number, columnIndex: {[string]: number}) => boolean)[];
    filterFunc: (settings: any, data: any, dataIndex: number) => void;
    callback: () => void;

    constructor(id: number, table: DataTables.Api, callback: () => void, sync_only:boolean=false) {
        this.table = table;
        this.id = id;
        this.functions = [];
        this.callback = callback;
        this.async = !sync_only;
        
        this.filters = this.getFilters(this.apply.bind(this));
        this.filterFunc = this.filterFunction.bind(this);
        $.fn.dataTable.ext.search.push(this.filterFunc);
    }

    getFilters(apply: () => void) {
        return {
            search: new Search(apply),
            dateRange: new DateRange(apply),
            tagSearch: new TagSearch(apply)
        }
    }

    toggleColumn(name: string, visible: ?boolean = undefined) {
        let column = this.table.column(this.columnIndexes[name]);
        visible = visible == undefined ? !column.visible() : visible;
        column.visible(visible);
    }

    getContexts(): FilterFunctions {
        if (this.callback) this.callback();
        
        return {
            search: this.filters.search.context,
            dateRange: this.filters.dateRange.context,
            tagSearch: this.filters.tagSearch.context
        }
    }

    add(column: ?string): FilterFunctions {
        Object.values(this.filters).forEach((filter: any) => {
            filter.prime(column, filter.addFilter);
        })
        return this.getContexts();
    }

    set(column: ?string): FilterFunctions {
        Object.values(this.filters).forEach((filter: any) => {
            filter.prime(column, filter.setFilter);
        })
        return this.getContexts();
    }

    clear(column: ?string): FilterFunctions {
        Object.values(this.filters).forEach((filter: any) => {
            filter.prime(column, filter.clearFilter);
        })
        return this.getContexts();
    }

    clearAll(column: ?string): void {
        Object.values(this.filters).forEach((filter: any) =>
            filter.prime(undefined, filter.clearFilter).call());
    }

    apply(isReload: boolean=false) {
        this.table.search('').columns().search('');
        this.functions = [];
        if (this.async && !isReload) {
            Object.values(this.filters).forEach((filter: any) => {
                let func = filter.asFunction();
                if (func) {
                    this.functions.push(func);
                } else {
                    filter.apply(this.table);
                }
            }, this);
        }
        this.table.draw();
    }

    dismount() {
        let funcIndex = $.fn.dataTable.ext.search.indexOf(this.filterFunc);
        if (funcIndex !== -1) {
            $.fn.dataTable.ext.search.splice(funcIndex, 1);
        }
    }

    filterFunction(settings: any, data: any, dataIndex: number) {
        return this.functions.every(f => f(settings, data, dataIndex, this.columnIndexes));
    }

    getDescription() {
        return (Object.values(this.filters)).reduce((array, filter: any) =>
            ({...array, ...filter.getDescription()}), {});
    }
}